import Investors from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseModule from '@components/course-landing-page/course-module'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import SkillsYouWillGain from '@components/course-landing-page/skills-you-will-gain'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import StudentProjects from '@components/course-landing-page/student-projects'
import TestimonialsFold from '@components/course-landing-page/testimonial'
import CodingClassCatalog from '@components/home/coding-class-catalog'
import CodingalOffering from '@components/home/codingal-offering'
import Teachers from '@components/home/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, COURSE_TITLES, FAQ } from '@lib/data/course-landing-page'
import { robloxMetaData } from '@lib/seo'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

const CourseCard = dynamic(() =>
  import('@components/course-landing-page/course-card')
)

const { ROBLOX } = COURSE_NAMES
const urlQuery = `?course=${
  COURSE_INFO.filter((course) => course.courseCode === 'rc')[0].trialCourseQuery
}`

const Roblox = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  useEffect(() => {
    const handleScroll = () => setShouldUpdateCourseCard(window.scrollY > 350)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...robloxMetaData} />
      <FAQPageJsonLd
        mainEntity={FAQ[ROBLOX].map(({ acceptedAnswerText, questionName }) => ({
          acceptedAnswerText: acceptedAnswerText,
          questionName: questionName,
        }))}
      />
      <CourseJsonLd
        courseName="ROBLOX"
        description="Learn to create and publish games on Roblox Studio. Codingal offers online 1:1 coding classes for Roblox with expert Computer Science teachers."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT36H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        shouldHideCourseCard={shouldUpdateCourseCard}
        courseName={ROBLOX}
        urlQuery={urlQuery}
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={ROBLOX} />
          <CourseModule
            courseName={ROBLOX}
            heading="Our Roblox Coding Class Curriculum"
          />
          <Teachers
            isCoursePage
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn Lua from{' '}
                <span className="yellow-underline">experienced teachers.</span>{' '}
                Our hand-picked coding tutors ensure the best learning
                experience.
              </Trans>
            }
          />
          <SkillsYouWillGain courseName={ROBLOX} urlQuery={urlQuery} />
          <CodingalOffering
            offeringType="prime"
            isCoursePage
            urlQuery={urlQuery}
          />
          <CodingCertificate
            title={
              <>
                Unlock your skills and earn your{' '}
                <span className="yellow-underline">
                  Roblox Developer certification
                </span>
              </>
            }
            description={
              "In this specialized Roblox Developer course, learners in grades 4 to 8 earn STEM.org-certified game development coding certificates that celebrate their achievements while unlocking special badges and leaderboard rankings, making programming for kids a fun and rewarding experience. Students can earn the Advance Roblox Game Developer and Basic Roblox Game Developer Certificates. These certifications are easy to download and share, are STEM.org accredited, and can be added to your child's learning portfolio for sharing with the world and on social media."
            }
          />
          <Activities courseName={ROBLOX} />
          <StudentProjects courseName={ROBLOX} />
          <StemAccredited
            courseName={COURSE_TITLES[ROBLOX]}
            urlQuery={urlQuery}
            heading="Roblox Coding provides students certificates accredited by STEM.org"
          />
          <TestimonialsFold
            courseName={ROBLOX}
            urlQuery={urlQuery}
            heading="Codingal’s training program is loved by students and trusted by parents"
          />
          <Faq courseName={ROBLOX} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['wd', 'aigc', 'pc']} />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={ROBLOX}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <CodingClassCatalog
        title={
          <>
            Our <span className="yellow-underline">Roblox Lua</span> course is
            appropriate for Middle School students
          </>
        }
        description={
          <>
            <p>
              Our Roblox Lua course is suitable for middle school students in
              grades 4 to 8. This course covers Roblox principles, including
              Roblox Studio, Lua, text-based scripting, creating a mini Obby
              game, learning about 2D platformers, developing a Tycoon game
              using advanced Roblox concepts, and creating a racing game and a
              Battle Royale game with mini-maps!
            </p>
            <p>
              Codingal supports kids ages 6 to 18 in grades K to 12 by providing
              engaging and comprehensive coding education that fosters
              creativity and critical thinking skills.
            </p>
          </>
        }
      />
      <FooterBanner
        title="Learn Roblox with best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default Roblox
